import { motion } from "framer-motion";
import { Scale, Lock, MessageSquare, Swords, Map, Bug } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { IMAGES } from "@/data/site";
import { PageBanner } from "@/components/PageBanner";

const rules = [
  {
    id: "r1",
    icon: Lock,
    title: "1. Responsabilità dell'Account",
    body: [
      {
        h: "1.1 — Sei l'unico responsabile del tuo account.",
        p: 'Ogni azione compiuta con il tuo profilo è imputabile a te, senza eccezioni. "Non ero io", "era mio fratello" o "mi hanno hackerato" non costituiscono giustificazioni valide né motivo di annullamento delle sanzioni.',
      },
      {
        h: "1.2 — Divieto assoluto di Alt Account.",
        p: "È severamente vietato l'utilizzo di account secondari (Alt) per: eludere una sanzione in corso, farmare risorse o vantaggi economici, votare/duplicare bonus, o aggirare qualsiasi limite del server. Gli Alt individuati verranno bannati insieme all'account principale.",
      },
      {
        h: "1.3 — La condivisione dell'account è a tuo rischio.",
        p: "Se scegli di condividere le tue credenziali con terzi, ti assumi la piena responsabilità di qualsiasi infrazione commessa. Eventuali ban derivanti da comportamenti di chi usa il tuo account NON verranno rimossi.",
      },
    ],
  },
  {
    id: "r2",
    icon: MessageSquare,
    title: "2. Comportamento e Chat",
    intro:
      "La chat è regolata dal buon senso e dal rispetto reciproco. La Giungla è una community per tutti: mantienila un luogo civile. È severamente vietato:",
    list: [
      "Tossicità e Flame: insulti gratuiti, provocazioni, istigazione a litigare o attacchi personali continuativi.",
      "Razzismo, omofobia e discriminazione: tolleranza zero. Qualsiasi contenuto discriminatorio comporta sanzioni immediate e severe.",
      "Filtri aggirati: eludere il filtro antiparolacce con simboli, spazi o caratteri alternativi equivale a violarlo del tutto.",
      "Spam, Flood e CAPS abusivo: ripetere messaggi, riempire la chat o scrivere interi messaggi in maiuscolo.",
      "Pubblicità esterna: promuovere altri server, canali, Discord o siti a scopo di lucro o reclutamento.",
      "Doxxing: condividere o minacciare di condividere informazioni personali di altri utenti. Una delle infrazioni più gravi in assoluto.",
      "Divieto assoluto di RMT (Real Money Trading): è tassativamente vietato acquistare, vendere o scambiare item, denaro di gioco, account o servizi in cambio di denaro reale o beni esterni al server. L'RMT comporta il ban permanente e IP Ban.",
    ],
  },
  {
    id: "r3",
    icon: Swords,
    title: "3. Meccaniche e Fair Play",
    intro:
      "La competizione su La Giungla SMP deve essere leale. Ogni forma di vantaggio artificiale è bandita.",
    body: [
      {
        h: "3.1 — Cheat e Client illegali (Ban immediato):",
        p: "Hack e client modificati di qualsiasi tipo (Kill Aura, Fly, Reach, Auto-Totem). X-Ray, resource pack \"trasparenti\" e texture che rivelano ore o entità. Baritone e qualsiasi automazione del movimento/mining. Macro e Auto-Clicker di ogni genere.",
      },
      {
        h: "3.2 — Limite CPS (Click Per Second):",
        p: "È consentito esclusivamente il click manuale, con un limite tecnico massimo di 15 CPS. Superare stabilmente questa soglia è considerato prova di auto-clicker e verrà sanzionato.",
      },
      {
        h: "3.3 — Attività AFK:",
        p: "È consentito unicamente l'AFK passivo (giocatore fermo, senza interazione). Ogni sistema che simuli attività per aggirare i controlli anti-AFK (macro anti-kick, pesi sul mouse, jump automatici) è vietato.",
      },
    ],
  },
  {
    id: "r4",
    icon: Bug,
    title: "4. Bug, Lag e Politica Rimborsi",
    body: [
      {
        h: "4.1 — Obbligo di segnalazione bug:",
        p: "Se scopri un bug o un exploit, sei obbligato a segnalarlo immediatamente allo staff tramite ticket su Discord. Sfruttare un bug per ottenere vantaggi comporta il ban e il reset totale dei progressi ottenuti illecitamente.",
      },
      {
        h: "4.2 — Divieto di Lag Machine:",
        p: "È vietato costruire meccanismi (redstone, spawner, entità in massa) progettati per generare lag e compromettere le prestazioni del server. Tali strutture verranno rimosse e l'autore sanzionato.",
      },
      {
        h: "4.3 — Politica Rimborsi:",
        p: "I rimborsi per la perdita di item vengono concessi esclusivamente in caso di crash o malfunzionamento verificato del server, e solo se supportati da prove video chiare e ininterrotte. Screenshot, testimonianze o \"ricordi\" non sono considerati prove valide.",
      },
    ],
  },
];

export default function Regolamento() {
  return (
    <div data-testid="regolamento-page">
      <PageBanner
        image={IMAGES.rules}
        icon={Scale}
        title="⚖️ Regolamento Ufficiale"
        subtitle="Le regole della Giungla non sono negoziabili."
        text="Giocare su La Giungla SMP implica l'accettazione totale e integrale del presente regolamento. L'ignoranza delle regole non costituisce giustificazione. Lo staff si riserva il diritto di intervenire, a propria discrezione, anche su casistiche non esplicitamente elencate ma contrarie allo spirito del server."
      />

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <Accordion type="single" collapsible className="space-y-4">
          {rules.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <AccordionItem
                value={r.id}
                data-testid={`rule-accordion-${r.id}`}
                className="border border-[#1b4d22]/50 bg-[#141b15] px-5 rounded-lg data-[state=open]:border-[#d4af37]/60 transition-colors"
              >
                <AccordionTrigger className="hover:no-underline py-5">
                  <span className="flex items-center gap-3 text-left font-heading font-bold uppercase text-base md:text-lg tracking-wide text-white">
                    <r.icon className="w-6 h-6 text-[#d4af37] shrink-0" />
                    {r.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-6">
                  {r.intro && (
                    <p className="text-[#c8d3cb] leading-relaxed mb-4">
                      {r.intro}
                    </p>
                  )}
                  {r.body && (
                    <div className="space-y-4">
                      {r.body.map((b, k) => (
                        <div key={k}>
                          <p className="text-[#d4af37] font-semibold mb-1">
                            {b.h}
                          </p>
                          <p className="text-[#c8d3cb] leading-relaxed text-sm md:text-base">
                            {b.p}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {r.list && (
                    <ul className="space-y-3 mt-2">
                      {r.list.map((item, k) => (
                        <li
                          key={k}
                          className="flex gap-3 text-[#c8d3cb] text-sm md:text-base leading-relaxed"
                        >
                          <span className="text-[#d4af37] mt-1">▸</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
